// Browser pricing engine — drives the SAME wasm binary the server runs (via
// wasmtime) through the raw memory ABI (spec §5.2). This is the keystone of the
// "single engine, no drift" guarantee: divergence between the operator's
// offline preview and the server commit is structurally impossible.
//
// The module's import section is empty (§4.1), so `WebAssembly.instantiate`
// takes no imports. Decimals cross as STRINGS (§3) — never as JS numbers.

interface WasmExports {
  memory: WebAssembly.Memory;
  alloc(len: number): number;
  dealloc(ptr: number, len: number): void;
  quote(inPtr: number, inLen: number, snapPtr: number, snapLen: number): bigint;
  engine_version(): bigint;
}

export interface PricingEngine {
  /** inputs_json + snapshot_json (decimals as strings) -> result_json string. */
  quote(inputsJson: string, snapshotJson: string): string;
  engineVersion(): string;
}

function unpack(packed: bigint): [number, number] {
  return [Number(packed >> 32n), Number(packed & 0xffffffffn)];
}

/** Instantiate the pricing core from its wasm bytes. The app fetches the bytes
 *  from the hashed asset URL; tests pass them from disk. */
export async function createEngine(bytes: BufferSource): Promise<PricingEngine> {
  const { instance } = await WebAssembly.instantiate(bytes, {}); // empty imports (§4.1)
  const ex = instance.exports as unknown as WasmExports;

  const writeStr = (s: string): [number, number] => {
    const data = new TextEncoder().encode(s);
    const ptr = ex.alloc(data.length);
    new Uint8Array(ex.memory.buffer, ptr, data.length).set(data);
    return [ptr, data.length];
  };
  const readStr = (ptr: number, len: number): string =>
    new TextDecoder().decode(new Uint8Array(ex.memory.buffer, ptr, len).slice());

  return {
    quote(inputsJson, snapshotJson) {
      const [ip, il] = writeStr(inputsJson);
      const [sp, sl] = writeStr(snapshotJson);
      const [rp, rl] = unpack(ex.quote(ip, il, sp, sl));
      const out = readStr(rp, rl);
      ex.dealloc(ip, il);
      ex.dealloc(sp, sl);
      ex.dealloc(rp, rl);
      return out;
    },
    engineVersion() {
      const [ptr, len] = unpack(ex.engine_version());
      const out = readStr(ptr, len);
      ex.dealloc(ptr, len);
      return out;
    },
  };
}
